import { SortDirection, SortingFn } from "@tanstack/react-table"
import { TrafficSummaryRowResponse } from "services/sicalcApi/responses/trafficSummaryListResponse"

/** The filter the server understands as "no filter", i.e. all traffic */
export const allTrafficFilter = '-'

/**
 * The filter grammar uses these to separate parameters and values, so a value
 * containing one of them cannot be expressed as a filter
 */
export const filterSeparators = [ ';', ',', '=' ]

/**
 * Look up a value in a dictionary returned by the server, falling back to a case
 * insensitive match. The casing of both parameter keys and scenario id keys on
 * the wire depends on the server's JSON serialiser settings.
 */
export function findByKey<TValue>(values: { [key: string]: TValue } | undefined, key: string) {
  if (values === undefined) {
    return undefined
  }
  if (values[key] !== undefined) {
    return values[key]
  }

  const matchingKey = Object.keys(values).find(k => k.toLowerCase() === key.toLowerCase())

  return matchingKey !== undefined ? values[matchingKey] : undefined
}

export const findParamValue = (row: TrafficSummaryRowResponse, paramKey: string) =>
  findByKey(row.paramValues, paramKey) ?? ''

/** Operations are annual, so decimals add noise rather than precision */
export const formatNumber = (value: number) => value.toLocaleString('nb-NO', { maximumFractionDigits: 0 })

export const formatScalingFactor = (value: number) => value.toLocaleString('nb-NO', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

export function norwegianTextSort<TRow>(): SortingFn<TRow> {
  return (rowA, rowB, columnId) =>
    String(rowA.getValue(columnId)).localeCompare(String(rowB.getValue(columnId)), 'nb', { numeric: true })
}

export const ariaSort = (sortDirection: SortDirection | false) => {
  if (sortDirection === 'asc') {
    return 'ascending' as const
  }
  if (sortDirection === 'desc') {
    return 'descending' as const
  }
  return 'none' as const
}

export const sortIndicatorClassName = (sortDirection: SortDirection | false) =>
  sortDirection === false
    ? undefined
    : `mdi mdi-arrow-${sortDirection === 'asc' ? 'up' : 'down'}`

/** A filter as a parameter key to accepted values map, or undefined for all traffic */
export type ParsedFilter = { [paramKey: string]: string[] } | undefined

/**
 * Parse "paramKey=value1,value2;paramKey2=value3" into a lookup.
 * ";" separates parameters (AND), "," separates values within one (OR).
 */
export function parseFilterString(filterString: string): ParsedFilter {
  if (!filterString || filterString.trim() === allTrafficFilter) {
    return undefined
  }

  const parsed: { [paramKey: string]: string[] } = {}

  for (const part of filterString.split(';')) {
    const separatorIndex = part.indexOf('=')
    if (separatorIndex < 0) {
      continue
    }
    const paramKey = part.slice(0, separatorIndex).trim()
    const values = part.slice(separatorIndex + 1)
      .split(',')
      .map(value => value.trim())
      .filter(value => value.length > 0)

    if (paramKey.length > 0 && values.length > 0) {
      parsed[paramKey] = values
    }
  }

  return parsed
}

/**
 * Build a filter from one or more categories. The categories are combined with
 * AND, the values within each with OR.
 */
export const buildFilterString = (criteria: { paramKey: string, values: string[] }[]) =>
  criteria.map(criterion => `${criterion.paramKey}=${criterion.values.join(',')}`).join(';')

/** True when every parameter in the filter accepts the row's value for it */
export function filterMatchesRow(filterString: string, row: TrafficSummaryRowResponse) {
  const parsed = parseFilterString(filterString)
  if (parsed === undefined) {
    return true
  }

  return Object.keys(parsed).every(paramKey => {
    const rowValue = findParamValue(row, paramKey)
    return parsed[paramKey].some(value => value.toLowerCase() === rowValue.toLowerCase())
  })
}
